import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Play, Pause, TrendingUp, Clock, Filter, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getTierColor = (tier) => {
    const map = {
        rookie: 'tier-rookie', contender: 'tier-contender', prospect: 'tier-prospect',
        pro: 'tier-pro', elite: 'tier-elite', legend: 'tier-legend',
    };
    return map[tier] || map.rookie;
};

const CANDY = ['#00E5FF', '#FF3DCB', '#8B5CFF'];

const getScoreColor = (score) => {
    if (score >= 9) return '#39FF14';
    if (score >= 7) return '#00E5FF';
    return '#71717A';
};

const PerformanceCard = ({ performance }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef(null);
    const hasAudio = !!performance.audio_url;

    const audioSrc = hasAudio
        ? performance.audio_url.startsWith('http')
            ? performance.audio_url
            : `${BACKEND_URL}${performance.audio_url}`
        : null;

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onEnd = () => { setIsPlaying(false); setProgress(0); };
        const onTime = () => setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
        audio.addEventListener('ended', onEnd);
        audio.addEventListener('timeupdate', onTime);
        return () => { audio.removeEventListener('ended', onEnd); audio.removeEventListener('timeupdate', onTime); };
    }, []);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) { audio.pause(); setIsPlaying(false); }
        else { audio.play().catch(() => {}); setIsPlaying(true); }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        if (hours < 1) return 'Just now';
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    /* Stable waveform heights — deterministic per performance so no re-render flicker */
    const waveHeights = useMemo(() => {
        const seed = performance.performance_id || 'default';
        return Array.from({ length: 48 }, (_, i) => {
            const c = seed.charCodeAt(i % seed.length) || 65;
            return 18 + ((c * 37 + i * 73) % 62);
        });
    }, [performance.performance_id]);

    const tierLabel = performance.artist_tier || 'rookie';

    return (
        <div
            className="candy-card rounded-2xl overflow-hidden hover:scale-[1.01] transition-all duration-300"
            data-testid={`performance-card-${performance.performance_id}`}
        >
            {audioSrc && <audio ref={audioRef} src={audioSrc} preload="none" />}

            {/* Gold top bar */}
            <div className="gold-bar" />

            {/* Header */}
            <div className="px-4 pt-5 pb-3 relative z-10">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <Link
                            to={`/performance/${performance.performance_id}`}
                            className="font-display font-bold text-base line-clamp-1 hover:text-primary transition-colors block"
                        >
                            {performance.title}
                        </Link>
                        <Link
                            to={`/profile/${performance.user_id}`}
                            className="text-sm font-semibold hover:opacity-80 transition-opacity block mt-0.5"
                            style={{ color: '#FFE000', fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            {performance.artist_name}
                        </Link>
                        {performance.location && (
                            <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                {performance.location}
                            </p>
                        )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-mono font-bold uppercase ${getTierColor(tierLabel)}`}>
                            {tierLabel}
                        </span>
                        {performance.performance_type === 'freestyle' && (
                            <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{color:'#2979FF', borderColor:'rgba(41,121,255,.35)', background:'rgba(41,121,255,.06)'}}>
                                Freestyle
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Waveform + Play */}
            <div className="relative h-20 mx-4 mb-0 flex items-center gap-3 relative z-10">
                {/* Waveform bars — lit up based on playback progress */}
                <div className="flex items-center gap-px flex-1 h-full py-1">
                    {waveHeights.map((h, i) => {
                        const barProgress = i / waveHeights.length;
                        const played = isPlaying && barProgress <= progress;
                        return (
                            <div
                                key={i}
                                className="flex-1 rounded-sm transition-opacity duration-75"
                                style={{
                                    height: `${h}%`,
                                    backgroundColor: CANDY[i % 3],
                                    opacity: played ? 0.9 : isPlaying ? 0.35 : 0.28,
                                }}
                            />
                        );
                    })}
                </div>
                {/* Play button */}
                <button
                    onClick={hasAudio ? togglePlay : undefined}
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
                    style={{
                        background: hasAudio ? '#39FF14' : '#27272a',
                        color: hasAudio ? '#04120a' : '#52525b',
                        boxShadow: isPlaying ? '0 0 22px rgba(57,255,20,.65)' : hasAudio ? '0 0 10px rgba(57,255,20,.35)' : 'none',
                        cursor: hasAudio ? 'pointer' : 'default',
                    }}
                    title={hasAudio ? undefined : 'No audio uploaded'}
                    data-testid={`play-btn-${performance.performance_id}`}
                >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </button>
            </div>

            {/* Scores */}
            {performance.judgment_count > 0 ? (
                <div className="px-4 pt-3 pb-4 relative z-10">
                    <div className="gold-divider mb-3" />
                    <div className="grid grid-cols-4 gap-1 mb-3">
                        {[
                            { label: 'Flow',     value: performance.avg_flow },
                            { label: 'Cad',      value: performance.avg_cadence },
                            { label: 'Del',      value: performance.avg_delivery },
                            { label: 'Clr',      value: performance.avg_clarity },
                        ].map((sc) => (
                            <div key={sc.label} className="text-center">
                                <p className="font-mono text-base font-bold" style={{ color: getScoreColor(sc.value) }}>
                                    {sc.value?.toFixed(1) ?? '—'}
                                </p>
                                <p className="text-[9px] text-zinc-500 uppercase tracking-wider mt-0.5">{sc.label}</p>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span
                                className="font-mono text-xl font-bold"
                                style={{ color: getScoreColor(performance.overall_score), textShadow: performance.overall_score >= 9 ? '0 0 12px rgba(57,255,20,.5)' : 'none' }}
                            >
                                {performance.overall_score?.toFixed(1) ?? '—'}
                            </span>
                            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">overall</span>
                        </div>
                        <span className="text-[10px] font-mono text-zinc-500">{performance.judgment_count} judged</span>
                    </div>
                </div>
            ) : (
                <div className="px-4 py-4 text-center relative z-10">
                    <div className="gold-divider mb-3" />
                    <p className="text-xs text-zinc-500 mb-1">No judgments yet</p>
                    <Link to={`/performance/${performance.performance_id}`} className="text-xs text-primary hover:underline">
                        Be the first to judge
                    </Link>
                </div>
            )}

            {/* Footer */}
            <div className="px-4 py-2 flex items-center justify-between border-t relative z-10" style={{borderColor:'rgba(255,255,255,.05)'}}>
                <span className="text-[10px] text-zinc-500 flex items-center gap-1 font-mono">
                    <Clock className="w-3 h-3" />
                    {formatDate(performance.created_at)}
                </span>
                {performance.tags?.length > 0 && (
                    <div className="flex items-center gap-1">
                        {performance.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-[9px] text-zinc-500 px-1.5 py-0.5 bg-white/5 rounded-full font-mono">
                                #{tag}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const FeedPage = () => {
    const [performances, setPerformances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('score');
    const [performanceType, setPerformanceType] = useState('all');

    useEffect(() => {
        fetchPerformances();
    }, [sortBy, performanceType]);

    const fetchPerformances = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ sort_by: sortBy, limit: '50' });
            if (performanceType !== 'all') {
                params.append('performance_type', performanceType);
            }
            const response = await axios.get(`${API}/performances?${params}`);
            setPerformances(response.data);
        } catch (error) {
            console.error('Failed to fetch performances:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl">
                        THE <span className="text-neon-blue">ARENA</span>
                    </h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        Performances ranked by skill, not popularity
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-36 bg-paper border-white/10 rounded-sm" data-testid="sort-select">
                            <TrendingUp className="w-4 h-4 mr-2 text-zinc-400" />
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent className="bg-paper border-white/10">
                            <SelectItem value="score">Top Rated</SelectItem>
                            <SelectItem value="recent">Most Recent</SelectItem>
                            <SelectItem value="judgments">Most Judged</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={performanceType} onValueChange={setPerformanceType}>
                        <SelectTrigger className="w-36 bg-paper border-white/10 rounded-sm" data-testid="type-select">
                            <Filter className="w-4 h-4 mr-2 text-zinc-400" />
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent className="bg-paper border-white/10">
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="freestyle">Freestyle</SelectItem>
                            <SelectItem value="acapella">Acapella</SelectItem>
                            <SelectItem value="over_beat">Over Beat</SelectItem>
                            <SelectItem value="verse">Verse</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Founding-era banner — true while the board is small; converts "empty" into "your shot" */}
            {!loading && performances.length < 12 && (
                <div className="glass-panel rounded-sm p-5 mb-6 border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <div className="font-mono text-[11px] uppercase tracking-widest text-neon-blue mb-1">Founding Era · Live now</div>
                            <h3 className="font-display font-bold text-lg sm:text-xl">The board is wide open.</h3>
                            <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
                                TrapWorld just opened. Every tier is unclaimed — the first artists to prove their bars
                                become the founding names on the wall. No pay-to-win. Skill &gt; Popularity. Get in early.
                            </p>
                            <Link to="/practice" className="inline-block mt-3 font-mono text-xs uppercase tracking-widest text-neon-blue hover:text-white transition-colors">
                                Just landed? Get instant AI feedback in the Practice Room — no waiting →
                            </Link>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <Link to="/upload">
                                <Button className="bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest rounded-sm">
                                    Drop a Track
                                </Button>
                            </Link>
                            <Link to="/register">
                                <Button variant="outline" className="border-white/15 font-bold uppercase tracking-widest rounded-sm">
                                    Join Free
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Grid */}
            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="glass-panel rounded-sm p-4">
                            <Skeleton className="h-6 w-3/4 mb-2 bg-white/5" />
                            <Skeleton className="h-4 w-1/2 mb-4 bg-white/5" />
                            <Skeleton className="h-24 w-full mb-4 bg-white/5" />
                            <div className="grid grid-cols-4 gap-2">
                                {Array.from({ length: 4 }).map((_, j) => (
                                    <Skeleton key={j} className="h-12 bg-white/5" />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : performances.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="performances-grid">
                    {performances.map((performance) => (
                        <PerformanceCard key={performance.performance_id} performance={performance} />
                    ))}
                </div>
            ) : (
                <div className="text-center py-20 glass-panel rounded-sm">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                        <TrendingUp className="w-8 h-8 text-zinc-600" />
                    </div>
                    <h3 className="font-display font-bold text-xl mb-2">No performances yet</h3>
                    <p className="text-zinc-400 mb-6">Be the first to drop some bars</p>
                    <Link to="/upload">
                        <Button className="bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest rounded-sm">
                            Upload Performance
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
};

export default FeedPage;
